import { createSelector } from 'reselect';
import type { RootState } from '../root-reducer';
import { isActiveCampaignHostSubscription } from '../../utils/campaignHostSubscriptionFormat';
import { resolveHostEntitlements } from '../../utils/planEntitlements';

const root = (state: RootState) => state.campaignHostSubscription;

export const campaignHostPricingSelector = createSelector(root, s => s.pricing);
export const campaignHostSubscriptionSelector = createSelector(root, s => s.subscription);
export const campaignHostCreationAccessSelector = createSelector(root, s => s.creationAccess);
export const campaignHostBillingHistorySelector = createSelector(root, s => s.billingHistory);

export const campaignHostCatalogPackagesSelector = createSelector(root, s =>
    Array.isArray(s.catalogPackages) ? s.catalogPackages : [],
);

export const campaignHostFetchingCatalogPackagesSelector = createSelector(root, s => s.fetchingCatalogPackages);

export const campaignHostPackagesSelector = createSelector(
    campaignHostPricingSelector,
    campaignHostCatalogPackagesSelector,
    (pricing, catalog) => {
        const fromPricing = pricing?.packages;
        if (Array.isArray(fromPricing) && fromPricing.length > 0) return fromPricing;
        return catalog;
    },
);

export const campaignHostLtdPromotionSelector = createSelector(
    campaignHostPricingSelector,
    pricing => pricing?.ltd_promotion ?? null,
);

export const campaignHostLtdPromoActiveSelector = createSelector(campaignHostLtdPromotionSelector, promo =>
    Boolean(promo?.enabled && promo?.active),
);

export const campaignHostFetchingMineSelector = createSelector(root, s => s.fetchingMine);

/** Active host plan — only after /mine has settled (avoids stale redux-persist subscription during fetch). */
export const hasActiveCampaignHostPlanSelector = createSelector(
    campaignHostSubscriptionSelector,
    campaignHostFetchingMineSelector,
    (sub, fetchingMine) => !fetchingMine && isActiveCampaignHostSubscription(sub),
);

export const hostEffectiveEntitlementsSelector = createSelector(
    campaignHostSubscriptionSelector,
    campaignHostFetchingMineSelector,
    (sub, fetchingMine) => {
        if (fetchingMine || !isActiveCampaignHostSubscription(sub)) {
            return {
                auto_upload: false,
                waived_platform_fee: false,
                instagram_automation: false,
                sms_notification: false,
                content_manager: false,
            };
        }
        const pkg = sub?.package;
        return resolveHostEntitlements(pkg?.entitlements, pkg?.effective_entitlements);
    },
);

export const hasHostAutoUploadEntitlementSelector = createSelector(
    hostEffectiveEntitlementsSelector,
    e => e.auto_upload === true,
);

export const hasHostInstagramAutomationEntitlementSelector = createSelector(
    hostEffectiveEntitlementsSelector,
    e => e.instagram_automation === true,
);

export const hasHostSmsNotificationEntitlementSelector = createSelector(
    hostEffectiveEntitlementsSelector,
    e => e.sms_notification === true,
);

export const campaignHostTierModalOpenSelector = createSelector(root, s => s.tierModalOpen);

export const campaignHostSubscriptionLoadingSelector = createSelector(root, s => s.fetchingPricing || s.fetchingMine);

export const campaignHostFetchingPricingSelector = createSelector(root, s => s.fetchingPricing);

export const campaignHostPaymentRailSelector = createSelector(
    campaignHostPricingSelector,
    pricing => pricing?.payment_rail ?? null,
);

export const campaignHostCheckingOutSelector = createSelector(root, s => s.checkingOut);

export const campaignHostEnterprisePackageSelector = createSelector(root, s => s.enterprisePackage);
export const campaignHostFetchingEnterprisePackageSelector = createSelector(root, s => s.fetchingEnterprisePackage);
export const campaignHostGrantingEnterpriseSelector = createSelector(root, s => s.grantingEnterprise);
export const campaignHostEnterpriseGrantResultSelector = createSelector(root, s => s.enterpriseGrantResult);
